import { ProductReturnRequirement } from 'API';
import { usePrevious } from 'hooks/use-previous';
import { getDefaultOrder } from 'mocks/order-entry.mock';
import { useInvoicingDetail } from 'providers/InvoicingDetailModuleProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CreatedOrder } from 'shared/models';
import { convertToCurrency, getOutboundApplicableCase } from 'shared/utils';
import InvoiceSummary from './InvoiceSummary';

/**
 * Component responsible for rendering the skeleton loading state for the invoice summary.
 *
 * @returns JSX.Element The skeleton loading state for the invoice summary.
 */
const SkeletonLoading = () => {
  return <InvoiceSummary isLoading={true} invoiceCase={getDefaultOrder()} shippingApplicableCase={undefined} />;
};

interface TotalInvoiceCase {
  orderNumber: string;
  totalInvoiceCodAmount: number;
}

interface InvoiceSummaryListsProps {
  productReturnRequirements: ProductReturnRequirement[];
}

/**
 * Component responsible for rendering the list of invoice summaries.
 *
 * @param productReturnRequirements - The product return requirements for each invoice case.
 */
const InvoiceSummaryLists = ({ productReturnRequirements }: InvoiceSummaryListsProps) => {
  const { invoiceCases } = useInvoicingDetail();
  const prevInvoiceCases = usePrevious(invoiceCases);
  const [totalInvoiceCases, setTotalInvoiceCases] = useState<Array<TotalInvoiceCase>>([]);

  // Memoized calculation to determine the case where shipping charges are applicable.
  const shippingApplicableCase = useMemo(() => {
    return getOutboundApplicableCase(invoiceCases, productReturnRequirements);
  }, [invoiceCases, productReturnRequirements]);

  const onAddTotalInvoiceCase = useCallback((orderNumber: string, totalInvoiceCodAmount: number) => {
    setTotalInvoiceCases(prev => {
      const index = prev.findIndex(item => item.orderNumber === orderNumber);
      if (index === -1) {
        return [...prev, { orderNumber, totalInvoiceCodAmount }];
      }
      return prev.map(item => (item.orderNumber === orderNumber ? { ...item, totalInvoiceCodAmount } : item));
    });
  }, []);

  const totalInvoiceCodAmount = useMemo(
    () =>
      totalInvoiceCases.reduce((acc, item) => {
        return acc + item.totalInvoiceCodAmount;
      }, 0),
    [totalInvoiceCases]
  );

  /**
   * Syncs totalInvoiceCases with invoiceCases by removing entries in totalInvoiceCases
   * whose orderNumber no longer exists in invoiceCases.
   * It avoids unnecessary updates when the invoiceCases array hasn't reduced in size.
   * On adding a new case to the invoiceCases the onAddTotalInvoiceCase will update the totalInvoiceCases
   */
  useEffect(() => {
    if (prevInvoiceCases?.length && prevInvoiceCases?.length > invoiceCases.length) return;
    setTotalInvoiceCases(prev =>
      prev.filter(item => invoiceCases.some(invoice => invoice.orderNumber === item.orderNumber))
    );
  }, [invoiceCases, prevInvoiceCases]);

  return (
    <div className="flex flex-col gap-6">
      {invoiceCases.map((invoiceCase: CreatedOrder) => (
        <InvoiceSummary
          key={invoiceCase.orderNumber}
          invoiceCase={invoiceCase}
          shippingApplicableCase={shippingApplicableCase}
          onAddTotalInvoiceCase={onAddTotalInvoiceCase}
        />
      ))}
      {!!totalInvoiceCodAmount && (
        <div>
          <div className="font-medium text-sm text-gray-500 pb-2 border-b border-gray-200 mb-2.5" />
          <div className="flex justify-end items-center">
            <div className="flex-grow text-sm font-bold text-right text-gray-700 mt-0.5">COD Total</div>
            <div className="w-24 font-bold text-gray-700 text-right" data-qa="codTotalLabel">
              {convertToCurrency(totalInvoiceCodAmount)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface RightPanelProps {
  isLoading: boolean;
  productReturnRequirements: ProductReturnRequirement[];
}
/**
 * Component responsible for rendering the right panel containing the invoice summary.
 *
 * @param isLoading - Flag indicating whether the component is in a loading state.
 * @param productReturnRequirements - The product return requirements for each invoice case.
 */
const RightPanel: React.FC<RightPanelProps> = ({ isLoading, ...props }) => {
  return (
    <div className="flex-none w-88 3xl:w-80 bg-white border-l p-6">
      <div className="font-medium text-gray-900 mb-3">Invoice Summary</div>
      {isLoading ? <SkeletonLoading /> : <InvoiceSummaryLists {...props} />}
    </div>
  );
};

export default RightPanel;
